import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Rating,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  ButtonAuthorizedWrapper,
  ConfirmationModal,
  FormAuthorizedWrapper,
  ManualLoader,
  Messages,
  ShadowCard,
} from "src/components";
import { getCurrentLease } from "src/helpers/helperLease";
import {
  useGetLeaseDetailsQuery,
  useGetMoveInConditionStatusQuery,
  useGetUserQuery,
  useSubmitMoveInConditionFormMutation,
} from "src/store/services/privateApi";
import {
  MoveInConditionFormSubmitPayload,
  MoveInConditionStatusResponse,
  MoveInExperienceRatings,
  MoveInSatisfactionSurveyPayload,
  MoveInUnitAmenityRatings,
} from "src/shared/models/containers/moveInCondition.model";
import tokens from "src/shared/theme/tokens";
import type {
  MoveInConditionItem,
  MoveInConditionSection,
} from "./moveInConditionSchema";
import {
  getMoveInConditionFormKind,
  getMoveInConditionSchema,
} from "./moveInConditionSchema";
import {
  formatMoveInDate as formatDate,
  getMoveInConditionAvailability,
} from "./moveInConditionAvailability";

type MoveInConditionFormValues = {
  sectionDescriptions: Record<string, string>;
  sectionItems: Record<string, Record<string, string>>;
  safetyItems: Record<string, string>;
  pestConcerns: string;
  specialProvisions: string;
  acknowledgmentAccepted: boolean;
  submittedOnBehalfOfUnit: boolean;
};

type MoveInConditionFormStep =
  | "intro"
  | "condition"
  | "acknowledgment"
  | "survey";

const LEGACY_SURVEY_QUESTIONS: Array<{
  id: keyof Pick<
    MoveInSatisfactionSurveyPayload,
    | "propertyTeam"
    | "apartmentSatisfaction"
    | "propertyCleanliness"
    | "moveInProcess"
    | "propertySatisfaction"
  >;
  title: string;
  prompt: string;
}> = [
  {
    id: "propertyTeam",
    title: "Property Team",
    prompt:
      "Has the team demonstrated friendliness, responsiveness, professionalism, and other positive attributes?",
  },
  {
    id: "apartmentSatisfaction",
    title: "Apartment Satisfaction",
    prompt:
      "How satisfied are you with the cleanliness and condition of your apartment at move-in?",
  },
  {
    id: "propertyCleanliness",
    title: "Property Cleanliness",
    prompt:
      "When you walk the property, are you pleased with the cleanliness and upkeep of the property?",
  },
  {
    id: "moveInProcess",
    title: "Move-In Process",
    prompt: "How organized was your move-in process?",
  },
  {
    id: "propertySatisfaction",
    title: "Property Satisfaction",
    prompt: "Would you refer your friends or family based on your experience?",
  },
];

const EXPERIENCE_QUESTIONS: Array<{
  id: keyof MoveInExperienceRatings;
  label: string;
}> = [
  {
    id: "teamCourteousOnTime",
    label: "Team members were courteous and on time",
  },
  {
    id: "apartmentReadyMetExpectations",
    label: "The apartment was in ready condition and met my expectations",
  },
  {
    id: "noImmediateIssues",
    label: "There were no immediate issues (e.g., broken appliances, damage)",
  },
  {
    id: "properPaperworkAndInstructions",
    label: "I received the proper paperwork and clear instructions",
  },
  { id: "questionsAnswered", label: "All my questions were answered" },
  {
    id: "areaTrashFree",
    label:
      "The area leading up to my apartment was trash free and clear of debris",
  },
  { id: "valetTrashBinProvided", label: "A valet trash bin was provided" },
];

const UNIT_AMENITY_QUESTIONS: Array<{
  id: keyof MoveInUnitAmenityRatings;
  label: string;
}> = [
  { id: "airConditioningHeating", label: "Air Conditioning/Heating" },
  { id: "appliances", label: "Appliances" },
  { id: "flooring", label: "Carpeting/Tile/Vinyl" },
  { id: "lightFixtures", label: "Light Fixtures" },
  { id: "paint", label: "Paint" },
];

const emptyExperienceRatings = (): MoveInExperienceRatings => ({
  teamCourteousOnTime: 0,
  apartmentReadyMetExpectations: 0,
  noImmediateIssues: 0,
  properPaperworkAndInstructions: 0,
  questionsAnswered: 0,
  areaTrashFree: 0,
  valetTrashBinProvided: 0,
});

const emptyUnitAmenityRatings = (): MoveInUnitAmenityRatings => ({
  airConditioningHeating: 0,
  appliances: 0,
  flooring: 0,
  lightFixtures: 0,
  paint: 0,
});

const emptySurveyRatings = (): MoveInSatisfactionSurveyPayload => ({
  propertyTeam: 0,
  apartmentSatisfaction: 0,
  propertyCleanliness: 0,
  moveInProcess: 0,
  propertySatisfaction: 0,
  recommendLikelihood: 0,
  communityAmenitiesSatisfaction: 0,
  keysReadyOnTime: null,
  mailboxKeyReceived: null,
  experienceRatings: emptyExperienceRatings(),
  unitAmenityRatings: emptyUnitAmenityRatings(),
  comments: "",
});

const averageSurveyScore = (survey: MoveInSatisfactionSurveyPayload) => {
  const values = [
    survey.propertyTeam,
    survey.apartmentSatisfaction,
    survey.propertyCleanliness,
    survey.moveInProcess,
    survey.propertySatisfaction,
  ];
  if (values.some((value) => value < 1 || value > 5)) return null;
  return (
    Math.round((values.reduce((sum, value) => sum + value, 0) / 5) * 10) / 10
  );
};

const isCompleteSurvey = (survey: MoveInSatisfactionSurveyPayload) => {
  if (averageSurveyScore(survey) == null) return false;
  if (
    survey.recommendLikelihood < 1 ||
    survey.recommendLikelihood > 10 ||
    survey.communityAmenitiesSatisfaction < 1 ||
    survey.communityAmenitiesSatisfaction > 10
  ) {
    return false;
  }
  if (survey.keysReadyOnTime == null || survey.mailboxKeyReceived == null) {
    return false;
  }
  const experienceValues = Object.values(survey.experienceRatings);
  const amenityValues = Object.values(survey.unitAmenityRatings);
  if (experienceValues.some((value) => value < 1 || value > 5)) return false;
  if (amenityValues.some((value) => value < 1 || value > 5)) return false;
  return true;
};

const yesNoLabel = (value: boolean | null | undefined) => {
  if (value == null) return "—";
  return value ? "Yes" : "No";
};

type ReadOnlyResident = {
  name: string;
  relation?: string;
  email?: string;
  phone?: string;
};

type MoveInConditionReviewNote = {
  group: string;
  label: string;
  value: string;
};

const defaultSectionDescriptions = (sections: MoveInConditionSection[]) =>
  sections.reduce<Record<string, string>>((acc, section) => {
    acc[section.id] = "";
    return acc;
  }, {});

const defaultSectionItems = (sections: MoveInConditionSection[]) =>
  sections.reduce<Record<string, Record<string, string>>>((acc, section) => {
    acc[section.id] = section.items.reduce<Record<string, string>>(
      (itemAcc, item) => {
        itemAcc[item.id] = "";
        return itemAcc;
      },
      {}
    );
    return acc;
  }, {});

const defaultSafetyItems = (safetyItems: MoveInConditionItem[]) =>
  safetyItems.reduce<Record<string, string>>((acc, item) => {
    acc[item.id] = "";
    return acc;
  }, {});

const schema = yup
  .object({
    acknowledgmentAccepted: yup
      .boolean()
      .oneOf([true], "Please accept the acknowledgment"),
    submittedOnBehalfOfUnit: yup
      .boolean()
      .oneOf(
        [true],
        "Confirm you are submitting this form for your unit. It can only be submitted once."
      ),
  })
  .required();

const cardTitleSx = {
  color: tokens.colors.ink,
  fontFamily: tokens.fonts.display,
  fontSize: { xs: 18, sm: 20 },
  fontWeight: 600,
  lineHeight: 1.25,
};

const sectionTitleSx = {
  color: tokens.colors.ink,
  fontFamily: tokens.fonts.display,
  fontSize: { xs: 17, sm: 18 },
  fontWeight: 600,
  lineHeight: 1.25,
};

const bodyCopySx = {
  color: tokens.colors.inkSecondary,
  fontFamily: tokens.fonts.body,
  fontSize: { xs: 14, sm: 15 },
  lineHeight: 1.55,
};

const legalCopySx = {
  ...bodyCopySx,
  fontSize: { xs: 13, sm: 14 },
};

const emphasizedLegalCopySx = {
  ...legalCopySx,
  color: tokens.colors.ink,
  fontStyle: "italic",
  fontWeight: 600,
};

const scrollToTop = () => {
  if (typeof window !== "undefined") {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "Pending";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not available";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
};

const personName = (person: any) =>
  [person?.FirstName, person?.LastName].filter(Boolean).join(" ") ||
  person?.fullName ||
  person?.Email ||
  "Resident";

const householdResidents = (
  userData: any,
  currentLease: any,
  leaseDetails: any
): ReadOnlyResident[] => {
  const leasePeople = leaseDetails?.people;

  if (Array.isArray(leasePeople) && leasePeople.length > 0) {
    return leasePeople.map((item: any) => ({
      name: personName(item.person),
      relation: item.relation,
      email: item.person?.Email,
      phone: item.person?.MobilePhone || item.person?.HomePhone,
    }));
  }

  const matchingLinks = userData?.user?.person?.leaseLinks?.filter(
    (link: any) => link?.lease?.id === currentLease?.id
  );

  if (Array.isArray(matchingLinks) && matchingLinks.length > 0) {
    return matchingLinks.map((link: any) => ({
      name: personName(userData.user.person),
      relation: link.HouseholdStatus,
      email: userData.user.person?.Email,
      phone:
        userData.user.person?.MobilePhone || userData.user.person?.HomePhone,
    }));
  }

  return userData?.user?.person
    ? [
        {
          name: personName(userData.user.person),
          email: userData.user.person?.Email,
          phone:
            userData.user.person?.MobilePhone ||
            userData.user.person?.HomePhone,
        },
      ]
    : [];
};

const collectReviewNotes = (
  payload: MoveInConditionFormSubmitPayload,
  sections: MoveInConditionSection[],
  safetyItems: MoveInConditionItem[],
  safetyTitle: string
): MoveInConditionReviewNote[] => {
  const notes: MoveInConditionReviewNote[] = [];

  payload.sections.forEach((section) => {
    const sectionDefinition = sections.find((item) => item.id === section.id);
    const sectionTitle = sectionDefinition?.title ?? section.id;
    const group = section.description
      ? `${sectionTitle} (${section.description})`
      : sectionTitle;

    section.items.forEach((item) => {
      if (!item.value) return;

      const itemDefinition = sectionDefinition?.items.find(
        (definition) => definition.id === item.id
      );

      notes.push({
        group,
        label: itemDefinition?.label ?? item.id,
        value: item.value,
      });
    });
  });

  payload.safetyItems.forEach((item) => {
    if (!item.value) return;

    const itemDefinition = safetyItems.find(
      (definition) => definition.id === item.id
    );

    notes.push({
      group: safetyTitle,
      label: itemDefinition?.label ?? item.id,
      value: item.value,
    });
  });

  if (payload.pestConcerns) {
    notes.push({
      group: safetyTitle,
      label: "Pest-related concerns",
      value: payload.pestConcerns,
    });
  }

  if (payload.specialProvisions) {
    notes.push({
      group: "Special Provisions",
      label: "Special provisions",
      value: payload.specialProvisions,
    });
  }

  return notes;
};

const residentsSummary = (residents: ReadOnlyResident[]) =>
  residents.map((resident) => resident.name).join(", ");

const LeaseSummaryCard = ({
  currentLease,
  residents,
  showResidentDetails = false,
}: {
  currentLease: any;
  residents: ReadOnlyResident[];
  showResidentDetails?: boolean;
}) => (
  <ShadowCard padding={{ xs: 1.5, sm: 4 }} marginBottom={{ xs: 2, sm: 6 }}>
    <Typography variant="h4Bold" mb={{ xs: 1, sm: 3 }} sx={cardTitleSx}>
      Lease Information
    </Typography>
    <Grid container spacing={{ xs: 1, sm: 3 }}>
      <Grid item xs={12} sm={4}>
        <Typography variant="caption1" sx={{ color: tokens.colors.inkMuted }}>
          Property
        </Typography>
        <Typography
          variant="body1"
          sx={{ ...bodyCopySx, color: tokens.colors.ink }}
        >
          {currentLease?.property?.name || "Not available"}
        </Typography>
      </Grid>
      <Grid item xs={6} sm={4}>
        <Typography variant="caption1" sx={{ color: tokens.colors.inkMuted }}>
          Unit
        </Typography>
        <Typography
          variant="body1"
          sx={{ ...bodyCopySx, color: tokens.colors.ink }}
        >
          {currentLease?.Unit || "Not available"}
        </Typography>
      </Grid>
      <Grid item xs={6} sm={4}>
        <Typography variant="caption1" sx={{ color: tokens.colors.inkMuted }}>
          Move-In Date
        </Typography>
        <Typography
          variant="body1"
          sx={{ ...bodyCopySx, color: tokens.colors.ink }}
        >
          {formatDate(currentLease?.MoveInDate)}
        </Typography>
      </Grid>
      {residents.length > 0 &&
        (showResidentDetails ? (
          residents.map((resident, index) => (
            <Grid item xs={12} sm={6} key={`${resident.name}-${index}`}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "gray.200",
                  borderRadius: 1,
                  p: { xs: 1.5, sm: 3 },
                }}
              >
                <Typography
                  variant="h3Bold"
                  sx={{ color: tokens.colors.ink }}
                  mb={1}
                >
                  {resident.name}
                </Typography>
                {resident.relation && (
                  <Typography variant="body2" sx={bodyCopySx}>
                    {resident.relation}
                  </Typography>
                )}
                {resident.email && (
                  <Typography variant="body2" sx={bodyCopySx}>
                    {resident.email}
                  </Typography>
                )}
                {resident.phone && (
                  <Typography variant="body2" sx={bodyCopySx}>
                    {resident.phone}
                  </Typography>
                )}
              </Box>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Box
              sx={{
                border: { xs: "none", sm: "1px solid" },
                borderColor: { sm: "gray.200" },
                borderRadius: 1,
                display: { xs: "flex", sm: "block" },
                gap: { xs: 1, sm: 0 },
                p: { xs: 0, sm: 3 },
              }}
            >
              <Typography
                variant="caption1"
                sx={{ color: tokens.colors.inkMuted, flexShrink: 0 }}
              >
                Residents
              </Typography>
              <Typography
                variant="body1"
                sx={{ ...bodyCopySx, color: tokens.colors.ink }}
              >
                {residentsSummary(residents)}
              </Typography>
            </Box>
          </Grid>
        ))}
    </Grid>
  </ShadowCard>
);

const MoveInConditionAcknowledgmentRecord = ({
  currentLease,
  onBack,
  residents,
  status,
}: {
  currentLease: any;
  onBack: () => void;
  residents: ReadOnlyResident[];
  status: MoveInConditionStatusResponse;
}) => {
  const countersigned = Boolean(status.countersigned);
  const resmanStatus = status.countersignedPdfResmanUploaded
    ? "Executed copy uploaded"
    : status.pdfResmanUploaded
    ? "Resident copy uploaded"
    : "Upload pending";

  return (
    <>
      <LeaseSummaryCard currentLease={currentLease} residents={residents} />

      <ShadowCard padding={{ xs: 2, sm: 4 }} marginBottom={{ xs: 3, sm: 6 }}>
        <Stack gap={3}>
          <Box>
            <Typography variant="h4Bold" mb={1} sx={cardTitleSx}>
              {status.origin === "window_expired"
                ? "Signing window closed"
                : "Acknowledgment Record"}
            </Typography>
            <Typography variant="body2" sx={bodyCopySx}>
              {status.origin === "window_expired"
                ? "The move-in condition form was not signed before the window closed. It is no longer available to edit. The property team completes the office signature."
                : "Your move-in condition form has been submitted for the unit and can only be submitted once. You can view this record. You cannot edit it."}
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="caption1"
                sx={{ color: tokens.colors.inkMuted }}
              >
                {status.origin === "window_expired"
                  ? "Resident signature"
                  : "Resident submitted"}
              </Typography>
              <Typography
                variant="body1"
                sx={{ ...bodyCopySx, color: tokens.colors.ink }}
              >
                {status.origin === "window_expired"
                  ? "Not signed"
                  : formatDateTime(status.createdAt)}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                variant="caption1"
                sx={{ color: tokens.colors.inkMuted }}
              >
                Resident acknowledgment
              </Typography>
              <Typography
                variant="body1"
                sx={{ ...bodyCopySx, color: tokens.colors.ink }}
              >
                {status.acknowledgmentAccepted
                  ? "Accepted electronically"
                  : "Not recorded"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                variant="caption1"
                sx={{ color: tokens.colors.inkMuted }}
              >
                Resident PDF
              </Typography>
              <Typography
                variant="body1"
                sx={{ ...bodyCopySx, color: tokens.colors.ink }}
              >
                {status.pdfGeneratedAt
                  ? `Generated ${formatDateTime(status.pdfGeneratedAt)}`
                  : "Not available yet"}
              </Typography>
              {status.pdfUrl && (
                <Button
                  href={status.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="text"
                  sx={{ mt: 0.5, px: 0 }}
                >
                  View resident copy
                </Button>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                variant="caption1"
                sx={{ color: tokens.colors.inkMuted }}
              >
                Office counter-signature
              </Typography>
              <Typography
                variant="body1"
                sx={{ ...bodyCopySx, color: tokens.colors.ink }}
              >
                {countersigned
                  ? `${status.countersignerName || "Office"} · ${formatDateTime(
                      status.countersignedAt
                    )}`
                  : "Awaiting office signature"}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                variant="caption1"
                sx={{ color: tokens.colors.inkMuted }}
              >
                Executed PDF
              </Typography>
              <Typography
                variant="body1"
                sx={{ ...bodyCopySx, color: tokens.colors.ink }}
              >
                {countersigned
                  ? status.countersignedPdfUrl
                    ? "Available"
                    : "Generating or not available yet"
                  : "Pending office signature"}
              </Typography>
              {status.countersignedPdfUrl && (
                <Button
                  href={status.countersignedPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="text"
                  sx={{ mt: 0.5, px: 0 }}
                >
                  View executed copy
                </Button>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                variant="caption1"
                sx={{ color: tokens.colors.inkMuted }}
              >
                ResMan record
              </Typography>
              <Typography
                variant="body1"
                sx={{ ...bodyCopySx, color: tokens.colors.ink }}
              >
                {resmanStatus}
              </Typography>
            </Grid>
          </Grid>
        </Stack>
      </ShadowCard>

      <ButtonAuthorizedWrapper>
        <Button
          variant="outlinedLarge"
          color="primary"
          fullWidth
          type="button"
          onClick={onBack}
        >
          Back to Requests
        </Button>
      </ButtonAuthorizedWrapper>
    </>
  );
};

const MoveInConditionReview = ({
  currentLease,
  isSubmitting,
  onBack,
  onSubmit,
  payload,
  residents,
  safetyItems,
  sections,
  safetyTitle,
}: {
  currentLease: any;
  isSubmitting: boolean;
  onBack: () => void;
  onSubmit: () => void;
  payload: MoveInConditionFormSubmitPayload;
  residents: ReadOnlyResident[];
  safetyItems: MoveInConditionItem[];
  sections: MoveInConditionSection[];
  safetyTitle: string;
}) => {
  const reviewNotes = collectReviewNotes(
    payload,
    sections,
    safetyItems,
    safetyTitle
  );
  const surveyAverage = averageSurveyScore(payload.satisfactionSurvey);

  return (
    <>
      <LeaseSummaryCard currentLease={currentLease} residents={residents} />

      <ShadowCard padding={{ xs: 2, sm: 4 }} marginBottom={{ xs: 3, sm: 6 }}>
        <Typography variant="h4Bold" mb={1} sx={cardTitleSx}>
          Move-In Satisfaction
        </Typography>
        <Typography variant="body2" sx={bodyCopySx} mb={2}>
          Average score: {surveyAverage ?? "—"} / 5
        </Typography>
        <Stack spacing={1.5}>
          <Typography variant="body2" sx={bodyCopySx}>
            Recommend likelihood:{" "}
            {payload.satisfactionSurvey.recommendLikelihood || "—"} / 10
          </Typography>
          <Typography variant="body2" sx={bodyCopySx}>
            Community amenities:{" "}
            {payload.satisfactionSurvey.communityAmenitiesSatisfaction || "—"} /
            10
          </Typography>
          <Typography variant="body2" sx={bodyCopySx}>
            Keys ready on time:{" "}
            {yesNoLabel(payload.satisfactionSurvey.keysReadyOnTime)}
          </Typography>
          <Typography variant="body2" sx={bodyCopySx}>
            Mailbox key received:{" "}
            {yesNoLabel(payload.satisfactionSurvey.mailboxKeyReceived)}
          </Typography>
          {LEGACY_SURVEY_QUESTIONS.map((question) => (
            <Box key={question.id}>
              <Typography
                variant="caption1"
                component="div"
                sx={{ color: tokens.colors.inkMuted }}
              >
                {question.title}
              </Typography>
              <Rating
                value={payload.satisfactionSurvey[question.id]}
                readOnly
                size="small"
              />
            </Box>
          ))}
          {EXPERIENCE_QUESTIONS.map((question) => (
            <Box key={question.id}>
              <Typography
                variant="caption1"
                component="div"
                sx={{ color: tokens.colors.inkMuted }}
              >
                {question.label}
              </Typography>
              <Rating
                value={
                  payload.satisfactionSurvey.experienceRatings[question.id]
                }
                readOnly
                size="small"
              />
            </Box>
          ))}
          {UNIT_AMENITY_QUESTIONS.map((question) => (
            <Box key={question.id}>
              <Typography
                variant="caption1"
                component="div"
                sx={{ color: tokens.colors.inkMuted }}
              >
                {question.label}
              </Typography>
              <Rating
                value={
                  payload.satisfactionSurvey.unitAmenityRatings[question.id]
                }
                readOnly
                size="small"
              />
            </Box>
          ))}
          {payload.satisfactionSurvey.comments?.trim() && (
            <Box>
              <Typography
                variant="caption1"
                component="div"
                sx={{ color: tokens.colors.inkMuted, mb: 0.5 }}
              >
                Suggestions
              </Typography>
              <Typography variant="body2" sx={bodyCopySx}>
                {payload.satisfactionSurvey.comments.trim()}
              </Typography>
            </Box>
          )}
        </Stack>
      </ShadowCard>

      <ShadowCard padding={{ xs: 2, sm: 4 }} marginBottom={{ xs: 3, sm: 6 }}>
        <Typography variant="h4Bold" mb={1} sx={cardTitleSx}>
          Issues You Reported
        </Typography>
        <Typography variant="body2" sx={bodyCopySx} mb={3}>
          {reviewNotes.length === 0
            ? "No issues were reported. Review the lease details before submitting."
            : `Review these ${reviewNotes.length} issue${
                reviewNotes.length === 1 ? "" : "s"
              } before submitting.`}
        </Typography>
        {reviewNotes.length === 0 ? (
          <Typography variant="body2" sx={bodyCopySx}>
            No defects, damage, safety issues, or pest concerns were reported.
          </Typography>
        ) : (
          reviewNotes.map((note, index) => (
            <Box key={`${note.group}-${note.label}-${index}`}>
              {index > 0 && <Divider sx={{ my: 2 }} />}
              <Typography
                variant="caption1"
                component="div"
                sx={{ color: tokens.colors.inkMuted, mb: 0.5 }}
              >
                {note.group}
              </Typography>
              <Typography
                variant="body1"
                component="div"
                sx={{
                  color: tokens.colors.ink,
                  fontFamily: tokens.fonts.body,
                  fontWeight: 600,
                  lineHeight: 1.35,
                  mb: 0.25,
                }}
              >
                {note.label}
              </Typography>
              <Typography variant="body2" component="div" sx={bodyCopySx}>
                {note.value}
              </Typography>
            </Box>
          ))
        )}
      </ShadowCard>

      <ButtonAuthorizedWrapper>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Button
              variant="outlinedLarge"
              color="primary"
              fullWidth
              onClick={onBack}
              disabled={isSubmitting}
            >
              Back to Edit
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <Button
              variant="containedLarge"
              color="primary"
              fullWidth
              onClick={onSubmit}
              disabled={isSubmitting}
            >
              Submit Move-In Condition Form
            </Button>
          </Grid>
        </Grid>
      </ButtonAuthorizedWrapper>
    </>
  );
};

export const MoveInConditionForm = () => {
  const navigate = useNavigate();
  const [formStep, setFormStep] = useState<MoveInConditionFormStep>("intro");
  const [reviewPayload, setReviewPayload] =
    useState<MoveInConditionFormSubmitPayload | null>(null);
  const [surveyRatings, setSurveyRatings] =
    useState<MoveInSatisfactionSurveyPayload>(emptySurveyRatings);
  const [surveyError, setSurveyError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [savedSubmissionId, setSavedSubmissionId] = useState("");
  const [savedPdfUrl, setSavedPdfUrl] = useState("");
  const [isCheckingSubmissionStatus, setIsCheckingSubmissionStatus] =
    useState(false);
  const saveReviewPayloadInFlightRef = useRef(false);

  const {
    data: userData,
    isLoading: isUserLoading,
    isError: isUserError,
  }: any = useGetUserQuery(null);
  const currentLease = getCurrentLease(userData);
  const { data: leaseDetails, isLoading: isLeaseDetailsLoading }: any =
    useGetLeaseDetailsQuery(currentLease?.id, {
      skip: !currentLease?.id,
    });
  const {
    data: moveInConditionStatus,
    isLoading: isMoveInConditionStatusLoading,
    refetch: refetchMoveInConditionStatus,
  } = useGetMoveInConditionStatusQuery(currentLease?.id ?? "", {
    skip: !currentLease?.id,
  });
  const [submitMoveInConditionForm, { isLoading: isSubmitting }] =
    useSubmitMoveInConditionFormMutation();
  const formKind = getMoveInConditionFormKind(currentLease?.property?.state);
  const conditionSchema = useMemo(
    () => getMoveInConditionSchema(formKind),
    [formKind]
  );
  const { safetyItems, sections } = conditionSchema;

  const residents = useMemo(
    () => householdResidents(userData, currentLease, leaseDetails),
    [currentLease, leaseDetails, userData]
  );
  const submissionAvailability = useMemo(
    () =>
      getMoveInConditionAvailability(
        currentLease?.MoveInDate,
        currentLease?.property?.state
      ),
    [currentLease?.MoveInDate, currentLease?.property?.state]
  );

  const defaultValues = useMemo<MoveInConditionFormValues>(
    () => ({
      sectionDescriptions: defaultSectionDescriptions(sections),
      sectionItems: defaultSectionItems(sections),
      safetyItems: defaultSafetyItems(safetyItems),
      pestConcerns: "",
      specialProvisions: "",
      acknowledgmentAccepted: false,
      submittedOnBehalfOfUnit: false,
    }),
    [safetyItems, sections]
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<MoveInConditionFormValues>({
    defaultValues,
    resolver: yupResolver(schema) as any,
  });

  const buildInventoryPayload = (
    values: MoveInConditionFormValues
  ): Omit<MoveInConditionFormSubmitPayload, "satisfactionSurvey"> => ({
    sections: sections.map((section) => ({
      id: section.id,
      description: values.sectionDescriptions[section.id]?.trim(),
      items: section.items.map((item) => ({
        id: item.id,
        value: values.sectionItems[section.id]?.[item.id]?.trim(),
      })),
    })),
    safetyItems: safetyItems.map((item) => ({
      id: item.id,
      value: values.safetyItems[item.id]?.trim(),
    })),
    acknowledgmentAccepted: values.acknowledgmentAccepted,
    submittedOnBehalfOfUnit: values.submittedOnBehalfOfUnit,
    ...(conditionSchema.showPestConcerns
      ? { pestConcerns: values.pestConcerns.trim() }
      : {}),
    ...(conditionSchema.showSpecialProvisions
      ? { specialProvisions: values.specialProvisions.trim() }
      : {}),
  });

  const continueToSurvey = handleSubmit(() => {
    setSurveyError("");
    setFormStep("survey");
    scrollToTop();
  });

  const continueToReview = handleSubmit((values) => {
    if (!isCompleteSurvey(surveyRatings)) {
      setSurveyError(
        "Please complete every survey question before continuing."
      );
      return;
    }

    const comments = surveyRatings.comments?.trim();
    setReviewPayload({
      ...buildInventoryPayload(values),
      satisfactionSurvey: {
        propertyTeam: surveyRatings.propertyTeam,
        apartmentSatisfaction: surveyRatings.apartmentSatisfaction,
        propertyCleanliness: surveyRatings.propertyCleanliness,
        moveInProcess: surveyRatings.moveInProcess,
        propertySatisfaction: surveyRatings.propertySatisfaction,
        recommendLikelihood: surveyRatings.recommendLikelihood,
        communityAmenitiesSatisfaction:
          surveyRatings.communityAmenitiesSatisfaction,
        keysReadyOnTime: surveyRatings.keysReadyOnTime,
        mailboxKeyReceived: surveyRatings.mailboxKeyReceived,
        experienceRatings: { ...surveyRatings.experienceRatings },
        unitAmenityRatings: { ...surveyRatings.unitAmenityRatings },
        ...(comments ? { comments } : {}),
      },
    });
    setSurveyError("");
    setErrorMessage("");
    scrollToTop();
  });

  const continueToConditionStep = () => {
    setFormStep("condition");
    scrollToTop();
  };

  const continueToAcknowledgment = () => {
    setFormStep("acknowledgment");
    scrollToTop();
  };

  const backToIntroStep = () => {
    setFormStep("intro");
    scrollToTop();
  };

  const backToConditionStep = () => {
    setFormStep("condition");
    scrollToTop();
  };

  const backToAcknowledgmentStep = () => {
    setFormStep("acknowledgment");
    scrollToTop();
  };

  const backToSurveyStep = () => {
    setReviewPayload(null);
    setFormStep("survey");
    scrollToTop();
  };

  const saveReviewPayload = async () => {
    if (
      !reviewPayload ||
      !currentLease?.id ||
      saveReviewPayloadInFlightRef.current
    ) {
      return;
    }

    saveReviewPayloadInFlightRef.current = true;
    setIsCheckingSubmissionStatus(true);

    try {
      if (moveInConditionStatus?.submitted) {
        setReviewPayload(null);
        setErrorMessage("");
        return;
      }

      try {
        const latestStatus = await refetchMoveInConditionStatus();
        if (latestStatus.data?.submitted) {
          setReviewPayload(null);
          setErrorMessage("");
          return;
        }
      } catch (error) {
        setErrorMessage(
          "Unable to confirm whether this move-in condition form was already submitted. Please refresh and try again."
        );
        return;
      }

      try {
        const response = await submitMoveInConditionForm({
          leaseId: currentLease.id,
          body: reviewPayload,
        }).unwrap();
        setSavedSubmissionId(response.id);
        setSavedPdfUrl(response.pdfUrl ?? "");
        setErrorMessage("");
      } catch (error: any) {
        setErrorMessage(
          error?.data?.message ??
            error?.data?.error ??
            "Unable to save the move-in condition form"
        );
      }
    } finally {
      setIsCheckingSubmissionStatus(false);
      saveReviewPayloadInFlightRef.current = false;
    }
  };

  if (
    isUserLoading ||
    isLeaseDetailsLoading ||
    isMoveInConditionStatusLoading ||
    isSubmitting
  ) {
    return <ManualLoader />;
  }

  if (isUserError || !currentLease?.id) {
    return (
      <ShadowCard padding={4} marginBottom={6}>
        <Typography variant="h4Bold" sx={cardTitleSx} mb={2}>
          Move-In Condition Form
        </Typography>
        <Typography variant="body2" sx={bodyCopySx}>
          We could not find an active lease for your account.
        </Typography>
      </ShadowCard>
    );
  }

  if (
    moveInConditionStatus?.submitted &&
    (!reviewPayload || !savedSubmissionId)
  ) {
    return (
      <MoveInConditionAcknowledgmentRecord
        currentLease={currentLease}
        residents={residents}
        status={moveInConditionStatus}
        onBack={() => navigate("/requests")}
      />
    );
  }

  if (reviewPayload) {
    return (
      <>
        {errorMessage !== "" && (
          <Messages
            messageHeading="Move-in condition form"
            messageContent={errorMessage}
            closeEvent={() => setErrorMessage("")}
            bottomMargin={6}
            topMargin={0}
          />
        )}
        <Box
          sx={{
            position: "relative",
            // Match FormAuthorizedWrapper mobile padding, sized for two stacked buttons.
            pb: { xs: "152px", sm: 0 },
          }}
        >
          <MoveInConditionReview
            currentLease={currentLease}
            isSubmitting={isSubmitting || isCheckingSubmissionStatus}
            payload={reviewPayload}
            residents={residents}
            safetyItems={safetyItems}
            safetyTitle={conditionSchema.safetyTitle}
            sections={sections}
            onBack={backToSurveyStep}
            onSubmit={saveReviewPayload}
          />
        </Box>
        {savedSubmissionId && (
          <ConfirmationModal
            onFormSubmit={() => navigate("/requests")}
            title="Success!"
            content={
              savedPdfUrl
                ? "Your move-in condition form was saved and a PDF copy was generated."
                : "Your move-in condition form was saved. The PDF copy is not available yet."
            }
            btnTitle="OK"
            hideClose={true}
          />
        )}
      </>
    );
  }

  const renewalBlocked = moveInConditionStatus?.window?.status === "renewal";

  if (renewalBlocked || !submissionAvailability.isAvailable) {
    return (
      <>
        <LeaseSummaryCard currentLease={currentLease} residents={residents} />
        <ShadowCard padding={{ xs: 2, sm: 4 }} marginBottom={{ xs: 3, sm: 6 }}>
          <Typography variant="h4Bold" sx={cardTitleSx} mb={2}>
            Move-In Condition Form
          </Typography>
          <Typography variant="body2" sx={bodyCopySx} mb={2}>
            {renewalBlocked
              ? moveInConditionStatus?.window?.message
              : submissionAvailability.message}
          </Typography>
          {!renewalBlocked &&
            submissionAvailability.moveInDateLabel &&
            submissionAvailability.dueDateLabel && (
              <Typography variant="body2" sx={bodyCopySx}>
                Submission window: {submissionAvailability.moveInDateLabel} to{" "}
                {submissionAvailability.dueDateLabel}
              </Typography>
            )}
        </ShadowCard>
        <ButtonAuthorizedWrapper>
          <Button
            variant="outlinedLarge"
            color="primary"
            fullWidth
            type="button"
            onClick={() => navigate("/requests")}
          >
            Back to Requests
          </Button>
        </ButtonAuthorizedWrapper>
      </>
    );
  }

  return (
    <FormAuthorizedWrapper className="dual_footer" onSubmit={continueToReview}>
      {formStep === "intro" && (
        <>
          <ShadowCard
            padding={{ xs: 1.5, sm: 4 }}
            marginBottom={{ xs: 2, sm: 6 }}
          >
            <Typography variant="h4Bold" mb={{ xs: 1, sm: 2 }} sx={cardTitleSx}>
              Inventory and Condition Form
            </Typography>
            <Typography variant="body2" sx={emphasizedLegalCopySx}>
              {conditionSchema.introText}
            </Typography>
          </ShadowCard>

          <LeaseSummaryCard
            currentLease={currentLease}
            residents={residents}
            showResidentDetails
          />

          <ButtonAuthorizedWrapper>
            <Button
              variant="containedLarge"
              color="primary"
              fullWidth
              type="button"
              sx={{ overflow: "hidden" }}
              onClick={continueToConditionStep}
            >
              Continue to Condition Notes
            </Button>
          </ButtonAuthorizedWrapper>
        </>
      )}

      {formStep === "condition" && (
        <>
          <Box mb={6}>
            {sections.map((section) => (
              <Accordion
                key={section.id}
                disableGutters
                sx={{
                  boxShadow: "none",
                  borderBottom: `1px solid ${tokens.colors.border}`,
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: 44,
                    px: { xs: 2, sm: 3 },
                    py: 0,
                    "& .MuiAccordionSummary-content": {
                      my: 1.5,
                    },
                  }}
                >
                  <Typography variant="h4Bold" sx={sectionTitleSx}>
                    {section.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {section.descriptionLabel && (
                    <TextField
                      label={section.descriptionLabel}
                      fullWidth
                      sx={{ mb: 3 }}
                      {...register(`sectionDescriptions.${section.id}` as any)}
                    />
                  )}
                  <Grid container spacing={3}>
                    {section.items.map((item) => (
                      <Grid item xs={12} md={6} key={item.id}>
                        <TextField
                          label={item.label}
                          multiline
                          minRows={2}
                          fullWidth
                          inputProps={{ maxLength: 1000 }}
                          {...register(
                            `sectionItems.${section.id}.${item.id}` as any
                          )}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          <ButtonAuthorizedWrapper>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlinedLarge"
                  color="primary"
                  fullWidth
                  type="button"
                  onClick={backToIntroStep}
                >
                  Back
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  variant="containedLarge"
                  color="primary"
                  fullWidth
                  type="button"
                  sx={{ overflow: "hidden" }}
                  onClick={continueToAcknowledgment}
                >
                  Continue to Safety & Acknowledgment
                </Button>
              </Grid>
            </Grid>
          </ButtonAuthorizedWrapper>
        </>
      )}

      {formStep === "acknowledgment" && (
        <>
          <ShadowCard
            padding={{ xs: 2, sm: 4 }}
            marginBottom={{ xs: 3, sm: 6 }}
          >
            <Typography variant="h4Bold" sx={cardTitleSx} mb={4}>
              {conditionSchema.safetyTitle}
            </Typography>
            <Grid container spacing={3}>
              {safetyItems.map((item) => (
                <Grid item xs={12} md={6} key={item.id}>
                  <TextField
                    label={item.label}
                    multiline
                    minRows={2}
                    fullWidth
                    inputProps={{ maxLength: 1000 }}
                    {...register(`safetyItems.${item.id}` as any)}
                  />
                </Grid>
              ))}
              {conditionSchema.showPestConcerns && (
                <Grid item xs={12}>
                  <TextField
                    label="Pest-related concerns"
                    multiline
                    minRows={3}
                    fullWidth
                    inputProps={{ maxLength: 2000 }}
                    {...register("pestConcerns")}
                  />
                </Grid>
              )}
            </Grid>
          </ShadowCard>

          <ShadowCard
            padding={{ xs: 2, sm: 4 }}
            marginBottom={{ xs: 3, sm: 6 }}
          >
            <Typography variant="h4Bold" sx={cardTitleSx} mb={3}>
              {conditionSchema.showSpecialProvisions
                ? "Special Provisions and Acknowledgment"
                : "Acknowledgment"}
            </Typography>
            {conditionSchema.showSpecialProvisions && (
              <TextField
                label="Special provisions"
                multiline
                minRows={4}
                fullWidth
                inputProps={{ maxLength: 4000 }}
                sx={{ mb: 4 }}
                {...register("specialProvisions")}
              />
            )}
            {conditionSchema.acknowledgmentText.map((paragraph, index) => (
              <Typography
                key={paragraph}
                variant="body2"
                mb={2}
                sx={index > 0 ? emphasizedLegalCopySx : legalCopySx}
              >
                {paragraph}
              </Typography>
            ))}
            <Controller
              control={control}
              name="acknowledgmentAccepted"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label="I accept this inventory as part of the Lease Contract."
                />
              )}
            />
            {errors.acknowledgmentAccepted?.message && (
              <Typography variant="body2" color="error.main" mb={2}>
                {errors.acknowledgmentAccepted.message}
              </Typography>
            )}
            <Controller
              control={control}
              name="submittedOnBehalfOfUnit"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label="I am submitting this form on behalf of my unit. It can only be submitted once."
                />
              )}
            />
            {errors.submittedOnBehalfOfUnit?.message && (
              <Typography variant="body2" color="error.main" mb={2}>
                {errors.submittedOnBehalfOfUnit.message}
              </Typography>
            )}
          </ShadowCard>

          <ButtonAuthorizedWrapper>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlinedLarge"
                  color="primary"
                  fullWidth
                  type="button"
                  onClick={backToConditionStep}
                >
                  Back
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  variant="containedLarge"
                  color="primary"
                  fullWidth
                  type="button"
                  sx={{ overflow: "hidden" }}
                  onClick={continueToSurvey}
                >
                  Continue to Satisfaction Survey
                </Button>
              </Grid>
            </Grid>
          </ButtonAuthorizedWrapper>
        </>
      )}

      {formStep === "survey" && (
        <>
          <ShadowCard
            padding={{ xs: 2, sm: 4 }}
            marginBottom={{ xs: 3, sm: 6 }}
          >
            <Typography variant="h4Bold" sx={cardTitleSx} mb={1}>
              Move-In Satisfaction Survey
            </Typography>
            <Typography variant="body2" sx={bodyCopySx} mb={3}>
              Please answer each question below. Star ratings use 1 (very
              dissatisfied) to 5 (very satisfied) unless noted otherwise.
            </Typography>
            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: tokens.colors.ink,
                    fontFamily: tokens.fonts.body,
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Overall
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: tokens.colors.ink,
                    fontFamily: tokens.fonts.body,
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How likely is it that you would recommend this community to a
                  friend or family member? *
                </Typography>
                <Typography variant="body2" sx={bodyCopySx} mb={1}>
                  Scale of 1 (not at all likely) to 10 (extremely likely).
                </Typography>
                <Rating
                  name="recommendLikelihood"
                  max={10}
                  value={surveyRatings.recommendLikelihood}
                  onChange={(_event, value) => {
                    setSurveyRatings((prev) => ({
                      ...prev,
                      recommendLikelihood: value ?? 0,
                    }));
                    setSurveyError("");
                  }}
                  size="large"
                />
              </Box>
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: tokens.colors.ink,
                    fontFamily: tokens.fonts.body,
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How satisfied are you with the community amenities? *
                </Typography>
                <Typography variant="body2" sx={bodyCopySx} mb={1}>
                  Scale of 1 (very dissatisfied) to 10 (very satisfied).
                </Typography>
                <Rating
                  name="communityAmenitiesSatisfaction"
                  max={10}
                  value={surveyRatings.communityAmenitiesSatisfaction}
                  onChange={(_event, value) => {
                    setSurveyRatings((prev) => ({
                      ...prev,
                      communityAmenitiesSatisfaction: value ?? 0,
                    }));
                    setSurveyError("");
                  }}
                  size="large"
                />
              </Box>

              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: tokens.colors.ink,
                    fontFamily: tokens.fonts.body,
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Move-in logistics
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" sx={bodyCopySx} mb={1}>
                      Were your keys ready at the time you were told they would
                      be? *
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={
                        surveyRatings.keysReadyOnTime == null
                          ? null
                          : surveyRatings.keysReadyOnTime
                          ? "yes"
                          : "no"
                      }
                      onChange={(_event, value) => {
                        if (value == null) return;
                        setSurveyRatings((prev) => ({
                          ...prev,
                          keysReadyOnTime: value === "yes",
                        }));
                        setSurveyError("");
                      }}
                    >
                      <ToggleButton value="yes">Yes</ToggleButton>
                      <ToggleButton value="no">No</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={bodyCopySx} mb={1}>
                      Did you receive your mailbox key upon move-in? *
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={
                        surveyRatings.mailboxKeyReceived == null
                          ? null
                          : surveyRatings.mailboxKeyReceived
                          ? "yes"
                          : "no"
                      }
                      onChange={(_event, value) => {
                        if (value == null) return;
                        setSurveyRatings((prev) => ({
                          ...prev,
                          mailboxKeyReceived: value === "yes",
                        }));
                        setSurveyError("");
                      }}
                    >
                      <ToggleButton value="yes">Yes</ToggleButton>
                      <ToggleButton value="no">No</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Stack>
              </Box>

              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: tokens.colors.ink,
                    fontFamily: tokens.fonts.body,
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Move-in experience
                </Typography>
                <Stack spacing={2}>
                  {EXPERIENCE_QUESTIONS.map((question) => (
                    <Box key={question.id}>
                      <Typography variant="body2" sx={bodyCopySx} mb={1}>
                        {question.label} *
                      </Typography>
                      <Rating
                        name={question.id}
                        value={surveyRatings.experienceRatings[question.id]}
                        onChange={(_event, value) => {
                          setSurveyRatings((prev) => ({
                            ...prev,
                            experienceRatings: {
                              ...prev.experienceRatings,
                              [question.id]: value ?? 0,
                            },
                          }));
                          setSurveyError("");
                        }}
                        size="large"
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: tokens.colors.ink,
                    fontFamily: tokens.fonts.body,
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Apartment amenities
                </Typography>
                <Stack spacing={2}>
                  {UNIT_AMENITY_QUESTIONS.map((question) => (
                    <Box key={question.id}>
                      <Typography variant="body2" sx={bodyCopySx} mb={1}>
                        {question.label} *
                      </Typography>
                      <Rating
                        name={question.id}
                        value={surveyRatings.unitAmenityRatings[question.id]}
                        onChange={(_event, value) => {
                          setSurveyRatings((prev) => ({
                            ...prev,
                            unitAmenityRatings: {
                              ...prev.unitAmenityRatings,
                              [question.id]: value ?? 0,
                            },
                          }));
                          setSurveyError("");
                        }}
                        size="large"
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: tokens.colors.ink,
                    fontFamily: tokens.fonts.body,
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Additional ratings
                </Typography>
                <Stack spacing={2}>
                  {LEGACY_SURVEY_QUESTIONS.map((question) => (
                    <Box key={question.id}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: tokens.colors.ink,
                          fontFamily: tokens.fonts.body,
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        {question.title} *
                      </Typography>
                      <Typography variant="body2" sx={bodyCopySx} mb={1}>
                        {question.prompt}
                      </Typography>
                      <Rating
                        name={question.id}
                        value={surveyRatings[question.id]}
                        onChange={(_event, value) => {
                          setSurveyRatings((prev) => ({
                            ...prev,
                            [question.id]: value ?? 0,
                          }));
                          setSurveyError("");
                        }}
                        size="large"
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>

              <TextField
                label="Additional suggestions (optional)"
                multiline
                minRows={3}
                fullWidth
                inputProps={{ maxLength: 2000 }}
                value={surveyRatings.comments ?? ""}
                onChange={(event) =>
                  setSurveyRatings((prev) => ({
                    ...prev,
                    comments: event.target.value,
                  }))
                }
              />
            </Stack>
            {surveyError && (
              <Typography variant="body2" color="error.main" mt={2}>
                {surveyError}
              </Typography>
            )}
          </ShadowCard>

          <ButtonAuthorizedWrapper>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlinedLarge"
                  color="primary"
                  fullWidth
                  type="button"
                  onClick={backToAcknowledgmentStep}
                >
                  Back
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  variant="containedLarge"
                  color="primary"
                  fullWidth
                  type="submit"
                  sx={{ overflow: "hidden" }}
                >
                  Review Move-In Condition Form
                </Button>
              </Grid>
            </Grid>
          </ButtonAuthorizedWrapper>
        </>
      )}
    </FormAuthorizedWrapper>
  );
};
